import { t } from 'i18next';
import { ActionItemDto, type PossibleActionDto } from '@/api/generator';
import type { I18nKey } from '@/utils/typings';
import { RouteLocationRaw } from 'vue-router';
import { mdiEye, mdiToggleSwitch, mdiToggleSwitchOff } from '@mdi/js';
import { bin, duplicate, edit as editIcon } from '@/components/icons/icons';
import { FormMode } from '@/components/forms/types';
import { mapValues } from 'lodash';
import { computed, MaybeRefOrGetter, toRef } from 'vue';
import { useTranslation } from 'i18next-vue';

export interface HasPossibleActions {
  possibleActions?: Array<PossibleActionDto> | null;
}

interface HasIsAvailable {
  isAvailable?: boolean;
}

export interface DefaultAction {
  text: string;
  icon: string;
  divider?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  tooltip?: string;
  to?: RouteLocationRaw;
  callback?: (event: MouseEvent | KeyboardEvent) => void | Promise<void>;
}

export type BaseItemDto = HasPossibleActions & HasIsAvailable;

export function getDefaultActions(mode?: FormMode, item?: BaseItemDto) {
  const availabilityFlags = item && getDisabledAndHiddenFlags(item, ActionItemDto.SetAvailability);

  const disable: DefaultAction = {
    get text() {
      return t('Disable');
    },
    icon: mdiToggleSwitch,
    ...(item && availabilityFlags
      ? {
          ...availabilityFlags,
          hidden: !item.isAvailable || availabilityFlags.hidden,
        }
      : {}),
  };

  const enable: DefaultAction = {
    get text() {
      return t('Enable');
    },
    icon: mdiToggleSwitchOff,
    ...(item && availabilityFlags
      ? {
          ...availabilityFlags,
          hidden: item.isAvailable || availabilityFlags.hidden,
        }
      : {}),
  };

  const view: DefaultAction = {
    get text() {
      return t('ViewDetail_other');
    },
    icon: mdiEye,
    ...(item ? getDisabledAndHiddenFlags(item, ActionItemDto.View) : {}),
  };

  const copy: DefaultAction = {
    get text() {
      return t('Copy');
    },
    icon: duplicate,
    ...(item ? getDisabledAndHiddenFlags(item, ActionItemDto.Edit) : {}),
    ...(mode && mode !== 'details' ? { hidden: true } : {}),
  };

  const edit: DefaultAction = {
    get text() {
      return t('Edit');
    },
    icon: editIcon,
    ...(item ? getDisabledAndHiddenFlags(item, ActionItemDto.Edit) : {}),
    ...(mode && mode !== 'details' ? { hidden: true } : {}),
  };

  const deleteAction: DefaultAction = {
    get text() {
      return t('Delete');
    },
    divider: true,
    icon: bin,
    ...(item ? getDisabledAndHiddenFlags(item, ActionItemDto.Delete) : {}),
    ...(mode && mode !== 'details' ? { hidden: true } : {}),
  };

  return {
    disable,
    enable,
    view,
    copy,
    edit,
    delete: deleteAction,
  };
}

// Returns boolean flags if to show or disable a context menu entry
// for a given 'action':
// hidden:
// Don't show the context menu entry when the entity doesn't contain
// a possible action with the value of 'action'.
// This means the user has no permission for this action.
// disabled:
// Set the context menu entry to disabled when there is no matching
// or only an entry with the flag isAllowed set to false.
// This means there is another reason like for example a predefined
// entity that prevents the user from editing the entity.
export function getDisabledAndHiddenFlags(entity: HasPossibleActions, action: ActionItemDto) {
  const possibleActionTarget = entity.possibleActions?.find(
    (possibleAction) => possibleAction.action === action,
  );

  const hidden = !entity.possibleActions || Boolean(!possibleActionTarget);

  const disabled = !!possibleActionTarget?.disabled;

  const tooltip = possibleActionTarget?.reason
    ? t(possibleActionTarget.reason.key as I18nKey, possibleActionTarget.reason.parameters || {})
    : undefined;

  return {
    hidden,
    disabled,
    tooltip,
  };
}

export function getActionNotPossibleReason(entity: HasPossibleActions, action: ActionItemDto) {
  return entity.possibleActions?.find((possibleAction) => possibleAction.action === action)?.reason;
}

export function isActionEnabled(entity: HasPossibleActions, action: ActionItemDto) {
  return !!entity.possibleActions?.some(
    (possibleAction) => possibleAction.action === action && !possibleAction.disabled,
  );
}

export function isActionVisible(entity: HasPossibleActions, action: ActionItemDto) {
  return !!entity.possibleActions?.some((possibleAction) => possibleAction.action === action);
}

export function usePossibleActions(entity: MaybeRefOrGetter<HasPossibleActions>) {
  const { t: translate } = useTranslation();

  const entityRef = toRef(entity);

  const possibleActions = computed(() =>
    mapValues(ActionItemDto, (action) => {
      const notPossibleReason = getActionNotPossibleReason(entityRef.value, action) ?? undefined;

      const reasonText = notPossibleReason
        ? translate(notPossibleReason.key as I18nKey, notPossibleReason.parameters || {})
        : undefined;

      return {
        enabled: isActionEnabled(entityRef.value, action),
        visible: isActionVisible(entityRef.value, action),
        reason: reasonText,
      };
    }),
  );

  function enabled(action: ActionItemDto) {
    return possibleActions.value[action].enabled;
  }

  function visible(action: ActionItemDto) {
    return possibleActions.value[action].visible;
  }

  function reason(action: ActionItemDto) {
    return possibleActions.value[action].reason;
  }

  return {
    possibleActions,
    enabled,
    visible,
    reason,
  };
}
